/**
 * BuilderCanvas — React Flow canvas with drag-and-drop support.
 * Handles node placement, connections, and selection.
 */
import React, { useCallback, useRef } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useReactFlow,
    ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import useBuilderStore from './store/builderStore';
import nodeTypes from './CustomNodes';

const CanvasInner = () => {
    const reactFlowWrapper = useRef(null);
    const { screenToFlowPosition } = useReactFlow();

    const nodes = useBuilderStore((s) => s.nodes);
    const edges = useBuilderStore((s) => s.edges);
    const onNodesChange = useBuilderStore((s) => s.onNodesChange);
    const onEdgesChange = useBuilderStore((s) => s.onEdgesChange);
    const onConnect = useBuilderStore((s) => s.onConnect);
    const addNode = useBuilderStore((s) => s.addNode);
    const selectNode = useBuilderStore((s) => s.selectNode);
    const clearSelection = useBuilderStore((s) => s.clearSelection);

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();
            const type = event.dataTransfer.getData('application/reactflow');
            if (!type) return;

            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            addNode(type, position);
        },
        [screenToFlowPosition, addNode]
    );

    const onNodeClick = useCallback(
        (_event, node) => {
            selectNode(node.id);
        },
        [selectNode]
    );

    const onPaneClick = useCallback(() => {
        clearSelection();
    }, [clearSelection]);

    return (
        <div
            ref={reactFlowWrapper}
            style={{ flex: 1, height: '100%' }}
        >
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                nodeTypes={nodeTypes}
                fitView
                proOptions={{ hideAttribution: true }}
                style={{ background: '#0F172A' }}
                defaultEdgeOptions={{
                    animated: true,
                    style: { stroke: '#6366F1', strokeWidth: 2 },
                    markerEnd: { type: 'arrowclosed', color: '#6366F1' },
                }}
            >
                <Background color="#334155" gap={20} size={1} />
                <Controls
                    style={{
                        background: '#1E293B',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.1)',
                    }}
                />
                <MiniMap
                    nodeColor={(node) => {
                        switch (node.type) {
                            case 'doqsolv': return '#6366F1';
                            case 'live_stamping': return '#10B981';
                            case 'esign': return '#3B82F6';
                            default: return '#6B7280';
                        }
                    }}
                    maskColor="rgba(15, 23, 42, 0.7)"
                    style={{
                        background: '#1E293B',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.1)',
                    }}
                />
            </ReactFlow>
        </div>
    );
};

const BuilderCanvas = () => (
    <ReactFlowProvider>
        <CanvasInner />
    </ReactFlowProvider>
);

export default BuilderCanvas;
